import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../rbc-overrides.css';
import Cookies from 'js-cookie';

import EventModal from '../components/Modal/EventModal';
import AddIcon from '@mui/icons-material/Add';
import 'moment/locale/ja';

moment.locale('ja');
const localizer = momentLocalizer(moment);

const EVENTS_API = 'https://system.sales-management.cloud/api/events.php';

const messages = {
  allDay: '終日',
  previous: '前',
  next: '次',
  today: '今日',
  month: '月',
  week: '週',
  day: '日',
  agenda: '予定リスト',
  date: '日付',
  time: '時間',
  event: 'イベント',
  noEventsInRange: 'この期間にイベントはありません',
  showMore: (total) => `+ ${total} 件`
};

function Event() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');

  // イベント一覧を取得
  const fetchEvents = useCallback(async (date = currentDate) => {
    setLoading(true);
    setError('');
    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      // 現在の月の開始日と終了日を取得
      const startOfMonth = moment(date).startOf('month').format('YYYY-MM-DD');
      const endOfMonth = moment(date).endOf('month').format('YYYY-MM-DD');

      const url = `${EVENTS_API}?start_date=${startOfMonth}&end_date=${endOfMonth}&limit=100`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        const formattedEvents = (data.data.events || []).map(event => {
          return {
            id: event.event_id,
            event_id: event.event_id,
            title: event.title,
            description: event.description,
            location: event.location,
            visibility: event.visibility,
            all_day: event.all_day,
            my_status: event.my_status,
            accepted_count: event.accepted_count,
            pending_count: event.pending_count,
            creator_user_id: event.creator_user_id,
            creator_color: event.creator_color,
            invites: event.invites || [],
            facility_id: event.facility_id,
            start: new Date(event.start_datetime.replace(' ', 'T')),
            end: new Date(event.end_datetime.replace(' ', 'T')),
            allDay: event.all_day,
            start_datetime: event.start_datetime,
            end_datetime: event.end_datetime
          };
        });
        setEvents(formattedEvents);
      } else {
        setError(data.message || 'イベントの取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // イベントの作成
  const handleCreate = async (formData) => {
    setModalLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const formatDateTime = (datetimeStr, isAllDay, isEnd = false) => {
        if (isAllDay) {
          // 終日の場合は日付のみを使用
          const dateOnly = datetimeStr.split('T')[0];
          return isEnd ? `${dateOnly} 23:59:59` : `${dateOnly} 00:00:00`;
        } else {
          // 通常の場合: YYYY-MM-DDTHH:MM → YYYY-MM-DD HH:MM:00
          const formatted = datetimeStr.replace('T', ' ');
          return formatted.length === 16 ? `${formatted}:00` : formatted;
        }
      };

      const requestBody = {
        title: formData.title,
        start_datetime: formatDateTime(formData.startDatetime, formData.allDay, false),
        end_datetime: formatDateTime(formData.endDatetime, formData.allDay, true),
        all_day: formData.allDay ? true : false,
        visibility: formData.visibility || 'private'
      };

      if (formData.description) requestBody.description = formData.description;
      if (formData.location) requestBody.location = formData.location;
      if (formData.facility_id && !formData.allDay) {
        requestBody.facility_id = parseInt(formData.facility_id);
      }
      if (formData.invitees && formData.invitees.length > 0) {
        requestBody.invitees = formData.invitees.map(id => parseInt(id));
      }

      const response = await fetch(EVENTS_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setSuccess(data.message || 'イベントを作成しました');
        setIsModalOpen(false);
        setSelectedEvent(null);
        fetchEvents();
      } else {
        if (response.status === 409) {
          setError('施設の予約が重複しています。別の時間帯または施設を選択してください。');
        } else {
          setError(data.message || 'イベントの作成に失敗しました');
        }
      }
    } catch (err) {
      console.error('Create error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setModalLoading(false);
    }
  };

  // イベントの更新
  const handleUpdate = async (formData) => {
    setModalLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const formatDateTime = (datetimeStr, isAllDay, isEnd = false) => {
        if (isAllDay) {
          // 終日の場合は日付のみを使用
          const dateOnly = datetimeStr.split('T')[0];
          return isEnd ? `${dateOnly} 23:59:59` : `${dateOnly} 00:00:00`;
        } else {
          // 通常の場合: YYYY-MM-DDTHH:MM → YYYY-MM-DD HH:MM:00
          const formatted = datetimeStr.replace('T', ' ');
          return formatted.length === 16 ? `${formatted}:00` : formatted;
        }
      };

      const requestBody = {
        id: selectedEvent.event_id || selectedEvent.id,
        title: formData.title,
        start_datetime: formatDateTime(formData.startDatetime, formData.allDay, false),
        end_datetime: formatDateTime(formData.endDatetime, formData.allDay, true),
        all_day: formData.allDay ? true : false,
        visibility: formData.visibility || 'private'
      };

      if (formData.description) requestBody.description = formData.description;
      if (formData.location) requestBody.location = formData.location;
      if (formData.facility_id && !formData.allDay) {
        requestBody.facility_id = parseInt(formData.facility_id);
      }
      if (formData.invitees && formData.invitees.length > 0) {
        requestBody.invitees = formData.invitees.map(id => parseInt(id));
      }

      const response = await fetch(EVENTS_API, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        if (response.status === 409) {
          setError('施設の予約が重複しています。別の時間帯または施設を選択してください。');
          setModalLoading(false);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      

      if (data.status === 'success') {
        setSuccess(data.message || 'イベントを更新しました');
        setIsModalOpen(false);
        setSelectedEvent(null);
        fetchEvents();
      } else {
        setError(data.message || 'イベントの更新に失敗しました');
      }
    } catch (err) {
      console.error('Update error:', err);
      setError(`通信エラーが発生しました: ${err.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  // イベントの削除
  const handleDelete = async (eventId) => {
    setModalLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const response = await fetch(`${EVENTS_API}?id=${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      

      if (data.status === 'success') {
        setSuccess('イベントを削除しました');
        setIsModalOpen(false);
        setSelectedEvent(null);
        fetchEvents();
      } else {
        setError(data.message || 'イベントの削除に失敗しました');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError(`通信エラーが発生しました: ${err.message}`);
    } finally {
      setModalLoading(false);
    }
  };

  // 招待応答（PATCHメソッド）
  const handleInviteResponse = async (eventId, responseStatus) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      const response = await fetch(EVENTS_API, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          event_id: eventId,
          status: responseStatus
        })
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setSuccess('招待に応答しました');
        setIsModalOpen(false);
        setSelectedEvent(null);
        fetchEvents();
      } else {
        setError(data.message || '招待応答に失敗しました');
      }
    } catch (err) {
      console.error('Invite response error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (formData) => {
    const isExistingEvent = Boolean(selectedEvent && (selectedEvent.event_id || selectedEvent.id));

    if (isExistingEvent) {
      handleUpdate(formData);
    } else {
      handleCreate(formData);
    }
  };

  const handleSelectEvent = async (event) => {
    try {
      const token = Cookies.get('auth_token');
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }

      // イベントの詳細情報を取得（invites情報を含む）
      const response = await fetch(`${EVENTS_API}?id=${event.event_id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.status === 'success' && data.data) {
        const eventDetail = {
          ...event,
          invites: data.data.invites || [],
          pending_count: data.data.pending_count || 0,
          accepted_count: data.data.accepted_count || 0,
          my_status: data.data.my_status
        };
        setSelectedEvent(eventDetail);
        setIsModalOpen(true);
      } else {
        setSelectedEvent(event);
        setIsModalOpen(true);
      }
    } catch (err) {
      console.error('Failed to fetch event detail:', err);
      // エラーがあっても基本情報でモーダルを開く
      setSelectedEvent(event);
      setIsModalOpen(true);
    }
  };

  const handleSelectSlot = (slotInfo) => {
    // クリックした日付で新規イベントを作成
    const newEvent = {
      event_id: null,
      title: '',
      description: '',
      location: '',
      facility_id: '',
      visibility: 'private',
      all_day: false,
      start_datetime: moment(slotInfo.start).format('YYYY-MM-DD HH:mm:ss'),
      end_datetime: moment(slotInfo.start).add(1, 'hour').format('YYYY-MM-DD HH:mm:ss'),
      invites: []
    };
    setSelectedEvent(newEvent);
    setIsModalOpen(true);
  };

  const handleNavigate = (date) => {
    setCurrentDate(date);
    fetchEvents(date);
  };

  const handleViewChange = (newView) => {
    setView(newView);
  };

  const calendarContainerHeight = view === 'month' ? '1100px' : '700px';

  const eventStyleGetter = (event) => {
    const visibilityColors = {
      private: '#6366f1',
      team: '#10b981',
      public: '#f59e0b'
    };

    const backgroundColor = visibilityColors[event.visibility] || '#6366f1';
    
    // 作成者の色を使用（左側の帯用）
    // 16進数カラーコードの妥当性チェック（#あり/なし両方をサポート）
    const isValidColor = (color) => {
      if (!color) return false;
      // #あり、または#なしの3桁/6桁の16進数
      return /^#?[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$/.test(color);
    };
    
    // #を追加（ない場合）
    const normalizeColor = (color) => {
      if (!color) return '#999999';
      return color.startsWith('#') ? color : `#${color}`;
    };
    
    const creatorColor = isValidColor(event.creator_color) 
      ? normalizeColor(event.creator_color) 
      : '#999999';

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.95,
        color: 'white',
        border: '0px',
        borderLeft: `4px solid ${creatorColor}`,
        display: 'block',
        fontSize: '12px',
        lineHeight: 1.3,
        padding: '2px 6px',
        paddingLeft: '8px',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden'
      }
    };
  };

  return (
    <div className="max-w-7xl mx-auto p-0 sm:p-4 md:p-6">
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">スケジュール</h1>
        <button
          onClick={() => {
            setSelectedEvent(null);
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 px-3 md:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm md:text-base"
        >
          <AddIcon />
          新規イベント
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {/* SPでmonth view時の注意喚起とクイック切替 */}
      {view === 'month' && (
        <div className="md:hidden mb-3 flex items-center justify-between rounded-md bg-amber-50 text-amber-700 px-3 py-2">
          <span className="text-xs">月表示はPC推奨です。週/日/予定リストをご利用ください。</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setView('agenda')} className="px-2 py-1 bg-amber-100 rounded text-xs">予定</button>
            <button onClick={() => setView('day')} className="px-2 py-1 bg-amber-100 rounded text-xs">日</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm md:shadow-md p-3 md:p-6 mx-0 sm:mx-0 md:mx-0" style={{ height: calendarContainerHeight }}>
        {loading ? (
          <div className="h-full flex items-center justify-center text-gray-500">
            読み込み中...
          </div>
        ) : (
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            allDayAccessor="allDay"
            style={{ height: '100%' }}
            onSelectEvent={handleSelectEvent}
            onSelectSlot={handleSelectSlot}
            selectable
            messages={messages}
            formats={{
              agendaHeaderFormat: ({ start, end }) => `${moment(start).format('YYYY年M月D日')} – ${moment(end).format('YYYY年M月D日')}`,
              agendaDateFormat: (date) => moment(date).format('M/D(ddd)'),
              agendaTimeFormat: (date) => moment(date).format('HH:mm'),
              eventTimeRangeFormat: ({ start, end }) => `${moment(start).format('HH:mm')}–${moment(end).format('HH:mm')}`
            }}
            eventPropGetter={eventStyleGetter}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            date={currentDate}
            view={view}
            views={['month', 'week', 'day', 'agenda']}
            showAllEvents
          />
        )}
      </div>

      <div className="mt-3 md:mt-4 flex gap-3 md:gap-4 text-xs md:text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#6366f1' }}></div>
          <span>非公開</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#10b981' }}></div>
          <span>チーム</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
          <span>公開</span>
        </div>
      </div>

      <EventModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedEvent(null);
        }}
        event={selectedEvent}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        onInviteResponse={handleInviteResponse}
        loading={modalLoading}
        allEvents={events}
      />
    </div>
  );
}

export default Event;